/**
 * Export Kit - Signal Intelligence Export System
 * 
 * Generates professional exports of signal data:
 * - PNG: Chart image for social sharing
 * - CSV: Raw data for spreadsheet analysis  
 * - JSON: Structured data for developers
 * - PDF: Full "Signal Intelligence Brief" report
 */

import { jsPDF } from 'jspdf';
import { SIGNAL_METADATA, CATEGORIES } from '../../api/capitulators.js';
import { getSignalIntelligence } from './signalIntelligence.js';

// ===========================================================================
// Configuration
// ===========================================================================

const BRAND = {
  name: 'CAPITULATORS.IO',
  tagline: 'Macro Financial Intelligence',
  twitter: '@CapitulationStn',
  url: 'capitulators.io',
};

// Terminal Gothic color palette (RGB for PDF)
// Matches the website's terminal-gothic.css exactly
const COLORS = {
  // Backgrounds
  bgMain: [14, 19, 23],            // #0E1317 - Deep Charcoal
  bgElevated: [18, 24, 29],        // #12181D
  bgCard: [24, 31, 38],            // #181F26
  
  // Primary palette
  fogGreen: [151, 184, 160],       // #97B8A0 - Primary accent
  lanternGold: [212, 168, 95],     // #D4A85F - Secondary accent
  rustRed: [178, 51, 42],          // #B2332A - Danger
  liquidityCyan: [0, 238, 229],    // #00EEE5 - Info highlights
  flashCrash: [255, 0, 51],        // #FF0033 - Extreme danger
  
  // Neutrals
  deadStone: [59, 63, 69],         // #3B3F45 - Borders
  ironRail: [92, 73, 63],          // #5C493F - Tertiary
  
  // Text
  textMain: [232, 237, 233],       // #E8EDE9
  textMuted: [151, 184, 160],      // #97B8A0 (fog green)
  textFaint: [107, 124, 113],      // #6B7C71
};

const STATUS_COLORS = {
  low: COLORS.fogGreen,
  normal: COLORS.fogGreen,
  neutral: COLORS.fogGreen,
  elevated: [249, 115, 22],        // Orange
  high: COLORS.rustRed,
  extreme: COLORS.flashCrash,
  tight: COLORS.rustRed,
  loose: COLORS.fogGreen,
};

// ===========================================================================
// Utility Functions
// ===========================================================================

function formatValue(value, signalId) {
  if (value == null) return '-';
  if (signalId === 'NET_LIQUIDITY') {
    return '$' + (value / 1e6).toFixed(2) + 'T';
  }
  if (signalId === 'LIQUIDITY_REGIME') return String(value).toUpperCase();
  if (Math.abs(value) >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (Math.abs(value) < 0.01) return value.toFixed(4);
  return value.toFixed(2);
}

function formatTimestamp() {
  return new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });
}

function getFilename(signalId, rangeId, extension) {
  const date = new Date().toISOString().split('T')[0];
  return signalId + '_' + rangeId + '_' + date + '.' + extension;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadText(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  downloadBlob(blob, filename);
}

// Simple QR Code pattern generator
function drawQRCode(pdf, url, x, y, size) {
  const cellSize = size / 21;
  
  // White background
  pdf.setFillColor(232, 237, 233);
  pdf.rect(x, y, size, size, 'F');
  
  // Draw finder patterns (corners)
  const drawFinder = (fx, fy) => {
    pdf.setFillColor(14, 19, 23);
    pdf.rect(fx, fy, cellSize * 7, cellSize * 7, 'F');
    pdf.setFillColor(232, 237, 233);
    pdf.rect(fx + cellSize, fy + cellSize, cellSize * 5, cellSize * 5, 'F');
    pdf.setFillColor(14, 19, 23);
    pdf.rect(fx + cellSize * 2, fy + cellSize * 2, cellSize * 3, cellSize * 3, 'F');
  };
  
  drawFinder(x + cellSize, y + cellSize);
  drawFinder(x + cellSize * 13, y + cellSize);
  drawFinder(x + cellSize, y + cellSize * 13);
  
  // Generate pseudo-random data pattern based on URL
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    hash = ((hash << 5) - hash) + url.charCodeAt(i);
    hash = hash & hash;
  }
  
  pdf.setFillColor(14, 19, 23);
  for (let row = 0; row < 21; row++) {
    for (let col = 0; col < 21; col++) {
      if ((row < 8 && col < 8) || (row < 8 && col > 12) || (row > 12 && col < 8)) continue;
      const val = (hash + row * 31 + col * 17) % 5;
      if (val < 2) {
        pdf.rect(x + col * cellSize, y + row * cellSize, cellSize * 0.9, cellSize * 0.9, 'F');
      }
    }
  }
}

// ===========================================================================
// PNG Export - Chart Image
// ===========================================================================

export async function exportPNG(svgElement, signal, rangeId) {
  return new Promise((resolve, reject) => {
    try {
      const svgRect = svgElement.getBoundingClientRect();
      const width = svgRect.width * 2;
      const height = svgRect.height * 2;
      
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      ctx.fillStyle = '#0E1317';
      ctx.fillRect(0, 0, width, height);
      
      const svgClone = svgElement.cloneNode(true);
      svgClone.setAttribute('width', width);
      svgClone.setAttribute('height', height);
      
      const svgData = new XMLSerializer().serializeToString(svgClone);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(svgUrl);
        
        ctx.fillStyle = '#6B7C71';
        ctx.font = '24px system-ui, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(BRAND.url, width - 20, height - 15);
        
        canvas.toBlob((blob) => {
          const filename = getFilename(signal.signal_id, rangeId, 'png');
          downloadBlob(blob, filename);
          resolve();
        }, 'image/png');
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        reject(new Error('Failed to load SVG'));
      };
      
      img.src = svgUrl;
    } catch (err) {
      reject(err);
    }
  });
}

// ===========================================================================
// CSV Export - Raw Data
// ===========================================================================

export function exportCSV(chartData, signal, rangeId) {
  const metadata = SIGNAL_METADATA[signal.signal_id] || {};
  
  const lines = [
    '# ' + BRAND.name + ' - Signal Data Export',
    '# Signal: ' + (signal.name || signal.signal_id),
    '# Description: ' + (signal.description || metadata.description || ''),
    '# Range: ' + rangeId.toUpperCase(),
    '# Generated: ' + formatTimestamp(),
    '# ' + BRAND.url,
    '',
    'date,value',
  ];
  
  chartData.forEach(point => {
    const date = new Date(point.time * 1000).toISOString().split('T')[0];
    lines.push(date + ',' + point.value);
  });
  
  const csv = lines.join('\n');
  const filename = getFilename(signal.signal_id, rangeId, 'csv');
  downloadText(csv, filename, 'text/csv');
}

// ===========================================================================
// JSON Export - Structured Data
// ===========================================================================

export function exportJSON(chartData, signal, rangeId, stats, narrative) {
  const metadata = SIGNAL_METADATA[signal.signal_id] || {};
  const intel = getSignalIntelligence(signal.signal_id);
  
  const exportData = {
    meta: {
      source: BRAND.name,
      url: BRAND.url,
      generated: new Date().toISOString(),
      version: '1.0',
    },
    signal: {
      id: signal.signal_id,
      name: signal.name || metadata.name,
      description: signal.description || metadata.description,
      category: metadata.category,
      formula: metadata.formula || null,
      inputs: metadata.inputs || [],
      frequency: metadata.frequency || 'Daily',
    },
    current: {
      value: signal.current?.value,
      formatted: formatValue(signal.current?.value, signal.signal_id),
      date: signal.current?.date,
      level: signal.current?.level,
      percentile: signal.current?.percentile,
      trend: signal.current?.trend,
    },
    range: {
      id: rangeId,
      high: stats?.high,
      low: stats?.low,
      percentile: stats?.percentile,
    },
    analysis: {
      narrative: narrative || null,
      interpretation: intel?.interpretation || null,
      whyItMatters: intel?.whyItMatters || null,
    },
    data: chartData.map(point => ({
      date: new Date(point.time * 1000).toISOString().split('T')[0],
      timestamp: point.time,
      value: point.value,
    })),
  };
  
  const json = JSON.stringify(exportData, null, 2);
  const filename = getFilename(signal.signal_id, rangeId, 'json');
  downloadText(json, filename, 'application/json');
}

// ===========================================================================
// PDF Export - Signal Intelligence Brief
// ===========================================================================

export async function exportPDF(svgElement, chartData, signal, rangeId, stats, narrative) {
  const metadata = SIGNAL_METADATA[signal.signal_id] || {};
  const intel = getSignalIntelligence(signal.signal_id);
  const statusColor = STATUS_COLORS[signal.current?.level] || COLORS.fogGreen;
  
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter',
  });
  
  // Set PDF metadata
  pdf.setProperties({
    title: 'Capitulators.io - ' + (signal.name || signal.signal_id) + ' Brief',
    author: 'Capitulators Team',
    subject: 'Signal Intelligence Brief',
    keywords: 'macro signals, liquidity index, Fed balance sheet, financial analysis',
    creator: 'Capitulators.io'
  });
  
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 40;
  const contentWidth = pageWidth - (margin * 2);
  let y = margin;
  
  const setColor = (rgb) => pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
  const setDrawColor = (rgb) => pdf.setDrawColor(rgb[0], rgb[1], rgb[2]);
  const setFillColor = (rgb) => pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
  
  // Background
  setFillColor(COLORS.bgMain);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // ===========================================================================
  // HEADER
  // ===========================================================================
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  setColor(COLORS.lanternGold);
  pdf.text(BRAND.name, margin, y + 14);
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  setColor(COLORS.textFaint);
  pdf.text('SIGNAL INTELLIGENCE BRIEF', pageWidth - margin, y + 10, { align: 'right' });
  
  y += 28;
  
  // Subtle gold gradient line
  setDrawColor(COLORS.lanternGold);
  pdf.setLineWidth(1);
  pdf.line(margin, y, pageWidth - margin, y);
  
  y += 24;
  
  // ===========================================================================
  // SIGNAL TITLE + STATUS
  // ===========================================================================
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  setColor(COLORS.textMain);
  pdf.text(signal.name || signal.signal_id, margin, y + 18);
  
  // Status badge
  const statusText = (signal.current?.level || 'NORMAL').toUpperCase();
  pdf.setFontSize(9);
  const statusWidth = pdf.getTextWidth(statusText) + 14;
  const statusX = pageWidth - margin - statusWidth;
  
  setFillColor(statusColor);
  pdf.roundedRect(statusX, y + 2, statusWidth, 18, 2, 2, 'F');
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  setColor(COLORS.bgMain);
  pdf.text(statusText, statusX + 7, y + 13);
  
  y += 28;
  
  // Description
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  setColor(COLORS.textMuted);
  const description = signal.description || metadata.description || '';
  const descLines = pdf.splitTextToSize(description, contentWidth);
  pdf.text(descLines, margin, y);
  y += descLines.length * 11 + 14;
  
  // ===========================================================================
  // CHART SECTION
  // ===========================================================================
  
  const chartHeight = 130;
  
  // Chart container with subtle border
  setFillColor(COLORS.bgElevated);
  setDrawColor(COLORS.deadStone);
  pdf.setLineWidth(0.5);
  pdf.roundedRect(margin, y, contentWidth, chartHeight, 3, 3, 'FD');
  
  // Render chart
  try {
    const chartImage = await svgToDataURL(svgElement);
    const padding = 6;
    pdf.addImage(
      chartImage, 
      'PNG', 
      margin + padding, 
      y + padding, 
      contentWidth - (padding * 2), 
      chartHeight - (padding * 2)
    );
  } catch (err) {
    setColor(COLORS.textFaint);
    pdf.setFontSize(10);
    pdf.text('Chart unavailable', margin + contentWidth/2, y + chartHeight/2, { align: 'center' });
  }
  
  y += chartHeight + 14;
  
  // ===========================================================================
  // METRICS TABLE
  // ===========================================================================
  
  const tableData = [
    { metric: 'Current Value', value: formatValue(signal.current?.value, signal.signal_id), color: COLORS.textMain },
    { metric: rangeId.toUpperCase() + ' High', value: formatValue(stats?.high, signal.signal_id), color: COLORS.rustRed },
    { metric: rangeId.toUpperCase() + ' Low', value: formatValue(stats?.low, signal.signal_id), color: COLORS.fogGreen },
    { metric: 'Percentile', value: (stats?.percentile || 0) + '%', color: COLORS.lanternGold },
  ];
  
  const rowHeight = 20;
  const headerHeight = 22;
  const tableHeight = tableData.length * rowHeight + headerHeight;
  const colWidth = contentWidth / 2;
  
  // Table container
  setFillColor(COLORS.bgElevated);
  setDrawColor(COLORS.deadStone);
  pdf.setLineWidth(0.5);
  pdf.roundedRect(margin, y, contentWidth, tableHeight, 3, 3, 'FD');
  
  // Table header
  setFillColor(COLORS.bgCard);
  pdf.roundedRect(margin + 0.5, y + 0.5, contentWidth - 1, headerHeight, 3, 0, 'F');
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  setColor(COLORS.lanternGold);
  pdf.text('METRIC', margin + 14, y + 14);
  pdf.text('VALUE', margin + colWidth + 14, y + 14);
  
  // Header bottom border
  setDrawColor(COLORS.deadStone);
  pdf.line(margin + 1, y + headerHeight, margin + contentWidth - 1, y + headerHeight);
  
  // Table rows
  tableData.forEach((row, i) => {
    const rowY = y + headerHeight + (i * rowHeight);
    
    // Alternating row background
    if (i % 2 === 1) {
      setFillColor(COLORS.bgCard);
      pdf.rect(margin + 1, rowY, contentWidth - 2, rowHeight, 'F');
    }
    
    // Metric name
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    setColor(COLORS.textFaint);
    pdf.text(row.metric, margin + 14, rowY + 14);
    
    // Value
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    setColor(row.color);
    pdf.text(row.value, margin + colWidth + 14, rowY + 14);
  });
  
  y += tableHeight + 16;
  
  // ===========================================================================
  // AI ANALYSIS (if available)
  // ===========================================================================
  
  if (narrative) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    setColor(COLORS.lanternGold);
    pdf.text('AI ANALYSIS', margin, y + 10);
    
    y += 18;
    
    // Analysis box
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    const narrativeLines = pdf.splitTextToSize(narrative, contentWidth - 20);
    const maxNarrativeLines = 5;
    const displayLines = narrativeLines.slice(0, maxNarrativeLines);
    const boxHeight = displayLines.length * 11 + 14;
    
    setFillColor(COLORS.bgElevated);
    setDrawColor(COLORS.lanternGold);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(margin, y, contentWidth, boxHeight, 3, 3, 'FD');
    
    setColor(COLORS.textMain);
    pdf.text(displayLines, margin + 10, y + 12);
    
    y += boxHeight + 14;
  }
  
  // ===========================================================================
  // CURRENT INTERPRETATION
  // ===========================================================================
  
  const currentInterp = intel?.interpretation?.find(i => i.level === signal.current?.level);
  if (currentInterp && y < pageHeight - 160) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    setColor(COLORS.fogGreen);
    pdf.text('CURRENT INTERPRETATION', margin, y + 10);
    
    y += 20;
    
    // Level and range on same line
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    setColor(statusColor);
    const levelText = (signal.current?.level || 'NORMAL').toUpperCase();
    pdf.text(levelText, margin, y);
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    setColor(COLORS.textFaint);
    const levelWidth = pdf.getTextWidth(levelText);
    pdf.text('  ' + currentInterp.range, margin + levelWidth, y);
    
    y += 14;
    
    // Meaning
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    setColor(COLORS.textMain);
    const meaningLines = pdf.splitTextToSize(currentInterp.meaning, contentWidth);
    pdf.text(meaningLines.slice(0, 2), margin, y);
    y += Math.min(meaningLines.length, 2) * 10 + 6;
    
    // Implication
    setColor(COLORS.textFaint);
    pdf.setFont('helvetica', 'italic');
    const implLines = pdf.splitTextToSize(currentInterp.implication, contentWidth);
    pdf.text(implLines.slice(0, 2), margin, y);
    y += Math.min(implLines.length, 2) * 10 + 12;
  }
  
  // ===========================================================================
  // WHY THIS MATTERS
  // ===========================================================================
  
  if (intel?.whyItMatters && y < pageHeight - 120) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    setColor(COLORS.lanternGold);
    pdf.text('WHY THIS MATTERS', margin, y + 10);
    
    y += 18;
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    setColor(COLORS.textMuted);
    const whyLines = pdf.splitTextToSize(intel.whyItMatters, contentWidth);
    const maxLines = Math.min(3, Math.floor((pageHeight - y - 90) / 10));
    pdf.text(whyLines.slice(0, maxLines), margin, y);
  }
  
  // ===========================================================================
  // FOOTER
  // ===========================================================================
  
  const footerY = pageHeight - 50;
  
  // Footer divider
  setDrawColor(COLORS.deadStone);
  pdf.setLineWidth(0.5);
  pdf.line(margin, footerY - 8, pageWidth - margin, footerY - 8);
  
  // Left column - Generation info
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  setColor(COLORS.textFaint);
  pdf.text('Generated: ' + formatTimestamp(), margin, footerY + 4);
  pdf.text('Data as of ' + (signal.current?.date || '-') + '  |  Powered by FRED + proprietary analysis', margin, footerY + 14);
  
  // Center - Branding
  setColor(COLORS.lanternGold);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.text(BRAND.url, pageWidth / 2, footerY + 4, { align: 'center' });
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  setColor(COLORS.textFaint);
  pdf.text(BRAND.twitter, pageWidth / 2, footerY + 14, { align: 'center' });
  
  // Right - QR Code
  const qrSize = 36;
  const qrX = pageWidth - margin - qrSize;
  const qrY = footerY - 4;
  drawQRCode(pdf, 'https://' + BRAND.url + '/signals/' + signal.signal_id, qrX, qrY, qrSize);
  
  // ===========================================================================
  // SAVE
  // ===========================================================================
  
  const filename = getFilename(signal.signal_id, rangeId, 'pdf');
  pdf.save(filename);
}

// Helper: Convert SVG to data URL with proper scaling
async function svgToDataURL(svgElement) {
  return new Promise((resolve, reject) => {
    const svgRect = svgElement.getBoundingClientRect();
    const scale = 3;
    const width = svgRect.width * scale;
    const height = svgRect.height * scale;
    
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    // Match chart background
    ctx.fillStyle = '#12181D';
    ctx.fillRect(0, 0, width, height);
    
    const svgClone = svgElement.cloneNode(true);
    svgClone.setAttribute('width', width);
    svgClone.setAttribute('height', height);
    svgClone.style.width = width + 'px';
    svgClone.style.height = height + 'px';
    
    if (!svgClone.getAttribute('viewBox')) {
      svgClone.setAttribute('viewBox', '0 0 ' + svgRect.width + ' ' + svgRect.height);
    }
    
    const svgData = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(svgUrl);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      reject(new Error('Failed to render SVG'));
    };
    img.src = svgUrl;
  });
}

// ===========================================================================
// Export Kit UI Component
// ===========================================================================

export function createExportDropdown(onExport) {
  const dropdown = document.createElement('div');
  dropdown.className = 'export-dropdown';
  dropdown.innerHTML = `
    <button class="export-dropdown__trigger" title="Export signal data">
      <span class="export-dropdown__icon">📤</span>
      <span class="export-dropdown__label">Export</span>
      <span class="export-dropdown__arrow">▾</span>
    </button>
    <div class="export-dropdown__menu">
      <button class="export-dropdown__item" data-format="png">
        <span class="export-dropdown__item-icon">🖼️</span>
        <span class="export-dropdown__item-text">
          <span class="export-dropdown__item-title">PNG Image</span>
          <span class="export-dropdown__item-desc">Chart for social sharing</span>
        </span>
      </button>
      <button class="export-dropdown__item" data-format="csv">
        <span class="export-dropdown__item-icon">📊</span>
        <span class="export-dropdown__item-text">
          <span class="export-dropdown__item-title">CSV Data</span>
          <span class="export-dropdown__item-desc">Raw data for Excel</span>
        </span>
      </button>
      <button class="export-dropdown__item" data-format="json">
        <span class="export-dropdown__item-icon">{ }</span>
        <span class="export-dropdown__item-text">
          <span class="export-dropdown__item-title">JSON Data</span>
          <span class="export-dropdown__item-desc">Structured data for devs</span>
        </span>
      </button>
      <div class="export-dropdown__divider"></div>
      <button class="export-dropdown__item export-dropdown__item--featured" data-format="pdf">
        <span class="export-dropdown__item-icon">📄</span>
        <span class="export-dropdown__item-text">
          <span class="export-dropdown__item-title">PDF Report</span>
          <span class="export-dropdown__item-desc">Full intelligence brief</span>
        </span>
        <span class="export-dropdown__item-badge">★</span>
      </button>
    </div>
  `;
  
  const trigger = dropdown.querySelector('.export-dropdown__trigger');
  
  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('export-dropdown--open');
  });
  
  document.addEventListener('click', () => {
    dropdown.classList.remove('export-dropdown--open');
  });
  
  dropdown.querySelectorAll('.export-dropdown__item').forEach(item => {
    item.addEventListener('click', async (e) => {
      e.stopPropagation();
      const format = item.dataset.format;
      dropdown.classList.remove('export-dropdown--open');
      
      item.classList.add('export-dropdown__item--loading');
      
      try {
        await onExport(format);
      } catch (err) {
        console.error('[Export] ' + format + ' failed:', err);
        alert('Export failed: ' + err.message);
      } finally {
        item.classList.remove('export-dropdown__item--loading');
      }
    });
  });
  
  return dropdown;
}
